import React, { useState, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight, Volume2, Square, BookOpen } from "lucide-react";
import { listenSection3 } from "../data/listen_section3";

const OPTION_LABELS = ["A", "B", "C", "D"];
const DIFFICULTY_STARS = { "J1+": 4, J1: 3, J2: 2, J3: 1 };

function DifficultyStars({ difficulty }) {
  const lit = DIFFICULTY_STARS[difficulty] ?? 2;
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={`text-sm ${i < lit ? "text-yellow-400" : "text-gray-200"}`}>
          ★
        </span>
      ))}
    </div>
  );
}

// Build the text that will be spoken for a question
function buildSpeechText(question) {
  const parts = [];
  parts.push(`${question.number}。`);
  parts.push(question.scene + "。");
  parts.push(...question.lines);
  parts.push("選択肢。");
  question.options.forEach((opt, i) => {
    parts.push(`${["エー", "ビー", "シー", "ディー"][i]}。${opt}。`);
  });
  return parts.join(" ");
}

export default function ListeningModuleView() {
  const questions = listenSection3;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSpeaking, setIsSpeaking] = useState(false);
  const utteranceRef = useRef(null);

  const question = questions[currentIndex];
  const userAnswer = answers[question?.id];
  const answered = userAnswer !== undefined;
  const isCorrect = answered && userAnswer === question?.answer;

  // ── Speech helpers ──────────────────────────────────────────
  const stopSpeech = useCallback(() => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  }, []);

  const speak = useCallback(
    (text) => {
      if (!window.speechSynthesis) return;
      stopSpeech();

      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = "ja-JP";
      utter.rate = 0.9;
      utter.pitch = 1;

      // Pick a Japanese voice if available
      const voices = window.speechSynthesis.getVoices();
      const jaVoice = voices.find((v) => v.lang.startsWith("ja"));
      if (jaVoice) utter.voice = jaVoice;

      utter.onstart = () => setIsSpeaking(true);
      utter.onend = () => setIsSpeaking(false);
      utter.onerror = () => setIsSpeaking(false);

      utteranceRef.current = utter;
      window.speechSynthesis.speak(utter);
    },
    [stopSpeech]
  );

  const handlePlay = () => {
    if (!question) return;
    speak(buildSpeechText(question));
  };

  // ── Navigation ───────────────────────────────────────────────
  function goTo(idx) {
    stopSpeech();
    setCurrentIndex(idx);
  }

  function handlePrev() {
    if (currentIndex > 0) goTo(currentIndex - 1);
  }

  function handleNext() {
    if (currentIndex < questions.length - 1) goTo(currentIndex + 1);
  }

  function handleSelect(optIdx) {
    if (!question || answered) return;
    setAnswers((prev) => ({ ...prev, [question.id]: optIdx }));
  }

  function handleReset() {
    stopSpeech();
    setAnswers({});
    setCurrentIndex(0);
  }

  // ── Stats ─────────────────────────────────────────────────────
  const correct = questions.filter((q) => answers[q.id] === q.answer).length;
  const attempted = questions.filter((q) => answers[q.id] !== undefined).length;
  const rate = attempted > 0 ? ((correct / attempted) * 100).toFixed(0) : "—";

  return (
    <div className="flex gap-4 flex-1 min-w-0">
      {/* ── Main column ─────────────────────────────────────── */}
      <div className="flex-1 min-w-0">
        {/* Section header */}
        <div className="mb-4 flex items-center gap-3">
          <div className="w-1 h-6 bg-orange-400 rounded-full" />
          <div>
            <h2 className="text-base font-bold text-gray-800">セクション３：総合聴解問題</h2>
            <p className="text-xs text-gray-400 mt-0.5">Q21–Q40 · 听音朗读后选择正确答案</p>
          </div>
        </div>

        {/* Question card */}
        <div className="border-2 border-orange-300 rounded-2xl overflow-hidden mb-3">
          {/* Nav bar */}
          <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-orange-100">
            <button
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="flex items-center gap-1 text-sm text-gray-500 px-3 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
            >
              <ChevronLeft size={14} /> 上一题
            </button>
            <span className="text-xs text-gray-400">
              {currentIndex + 1} / {questions.length}
            </span>
            <button
              onClick={handleNext}
              disabled={currentIndex >= questions.length - 1}
              className="flex items-center gap-1 text-sm text-gray-500 px-3 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
            >
              下一题 <ChevronRight size={14} />
            </button>
          </div>

          {/* Body */}
          <div className="bg-gradient-to-b from-orange-50 to-white px-8 py-5 min-h-44">
            {question && (
              <div>
                {/* Number + TTS controls */}
                <div className="flex items-center justify-between mb-3">
                  <p className="font-bold text-gray-800">{question.number}</p>
                  <div className="flex items-center gap-2">
                    {isSpeaking ? (
                      <button
                        onClick={stopSpeech}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-red-300 text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                      >
                        <Square size={12} /> 停止朗读
                      </button>
                    ) : (
                      <button
                        onClick={handlePlay}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors"
                      >
                        <Volume2 size={12} /> 朗读题目
                      </button>
                    )}
                  </div>
                </div>

                {/* Scene label */}
                <p className="text-xs text-gray-400 mb-2 italic">{question.scene}</p>

                {/* Lines — hidden until answered (simulates real listening test) */}
                {answered ? (
                  <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs font-semibold text-amber-700 mb-1.5">原文（答题后显示）</p>
                    {question.lines.map((line, i) => (
                      <p key={i} className="text-sm text-gray-700 leading-relaxed mb-0.5">
                        {line}
                      </p>
                    ))}
                  </div>
                ) : (
                  <div className="mb-3 p-3 bg-gray-50 border border-dashed border-gray-200 rounded-lg">
                    <p className="text-xs text-gray-400 text-center">点击「朗读题目」收听音频，然后选择答案</p>
                    <p className="text-xs text-gray-300 text-center mt-1">（原文将在作答后显示）</p>
                  </div>
                )}

                {/* Options */}
                <div className="space-y-2">
                  {question.options.map((opt, i) => {
                    const label = OPTION_LABELS[i];
                    const isSelected = userAnswer === i;
                    const isCorrectOpt = question.answer === i;

                    let style = "border-gray-200 text-gray-700 bg-white hover:bg-gray-50";
                    if (answered) {
                      if (isCorrectOpt) style = "border-green-400 bg-green-50 text-green-700";
                      else if (isSelected) style = "border-red-400 bg-red-50 text-red-600";
                    } else if (isSelected) {
                      style = "border-orange-400 bg-orange-50 text-orange-700";
                    }

                    return (
                      <button
                        key={i}
                        onClick={() => handleSelect(i)}
                        disabled={answered}
                        className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg border text-sm transition-colors ${style} disabled:cursor-default`}
                      >
                        <span
                          className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                            answered && isCorrectOpt
                              ? "bg-green-500 text-white"
                              : answered && isSelected
                              ? "bg-red-400 text-white"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {label}
                        </span>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Feedback */}
        {answered && question && (
          <div className="border border-gray-200 rounded-xl px-4 py-3 mb-3 bg-white">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm text-gray-600">
                正确答案{" "}
                <strong className="text-green-600">{OPTION_LABELS[question.answer]}</strong>
                　您选择{" "}
                <strong className={isCorrect ? "text-green-600" : "text-red-500"}>
                  {OPTION_LABELS[userAnswer]}
                </strong>
                {isCorrect ? (
                  <span className="ml-1 text-green-500">✓</span>
                ) : (
                  <span className="ml-1 text-red-400">✗</span>
                )}
              </span>
              <div className="flex items-center gap-1 text-xs text-gray-400 ml-auto">
                <BookOpen size={12} />
                <span>{question.knowledgePoint}</span>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                难度 <DifficultyStars difficulty={question.difficulty} />
              </span>
            </div>
            {question.explanation && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs font-semibold text-indigo-600 mb-1.5">AI解析</p>
                <p className="text-xs text-gray-600 leading-relaxed">{question.explanation}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Right: answer sheet ──────────────────────────────── */}
      <div className="w-52 shrink-0">
        <div className="border border-gray-200 rounded-xl p-3 bg-white sticky top-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-gray-600">答题卡</span>
            <button
              onClick={handleReset}
              className="text-xs text-gray-400 hover:text-red-500 transition-colors"
            >
              重置
            </button>
          </div>

          {/* Stats */}
          <div className="flex gap-2 mb-3 text-center">
            <div className="flex-1 bg-green-50 rounded-lg py-1.5">
              <p className="text-lg font-bold text-green-600">{correct}</p>
              <p className="text-[10px] text-gray-400">正确</p>
            </div>
            <div className="flex-1 bg-gray-50 rounded-lg py-1.5">
              <p className="text-lg font-bold text-gray-500">{attempted}</p>
              <p className="text-[10px] text-gray-400">已答</p>
            </div>
            <div className="flex-1 bg-indigo-50 rounded-lg py-1.5">
              <p className="text-lg font-bold text-indigo-500">{rate}%</p>
              <p className="text-[10px] text-gray-400">正确率</p>
            </div>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-4 gap-1">
            {questions.map((q, idx) => {
              const ua = answers[q.id];
              const isAnswered = ua !== undefined;
              const isCur = idx === currentIndex;
              const isOk = isAnswered && ua === q.answer;

              let bg = "bg-gray-100 text-gray-400";
              if (isCur) bg = "bg-orange-400 text-white";
              else if (isAnswered) bg = isOk ? "bg-green-400 text-white" : "bg-red-400 text-white";

              return (
                <button
                  key={q.id}
                  onClick={() => goTo(idx)}
                  className={`h-8 rounded text-xs font-medium transition-colors ${bg}`}
                >
                  {q.id}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-3 flex flex-col gap-1 text-[10px] text-gray-400">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-orange-400 inline-block" /> 当前题
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-green-400 inline-block" /> 回答正确
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded bg-red-400 inline-block" /> 回答错误
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
